import { useEffect, useCallback } from "react";
import { useProject } from "../../../../hooks";
import { useCart, useLabor as useLaborQuery } from "../../../../hooks/query";
import { useLaborFormulaEvaluator } from "../../hooks";

const LABOR_ORDER = [
    "Tear off/ Install",
    "Additional Layers",
    "Wood Tear off",
    "Mod Bit Tear off install",
    "Ice & Water Shield",
    "Install Decking",
    "Install decking - steep",
    "Steep Charge",
    "Hand carry Shingles",
    "Gutter Removal",
    "Flash Chimney (Brick)",
    "Flash Chimney (Wood)",
    "Flash Cooler",
    "Replace Skylight (Curb)",
    "Replace Skylight (Deck)",
    "Flash Skylight",
    "RV cut in/Patch",
    "Ridge vent cut in per LF",
    "Build Cricket",
    "Carry bundle up ladder",
    "Trip Charge",
    "No Access",
    "Gutter Install",
    "Downspout install",
    "Gutter Screens",
    "Other Gutter/DS/ETC",
    "EPDM",
    "Solar",
    "Prep&Prime paint Exterior",
    "Handrail",
    "Deck",
    "Trim, Soffit & Siding",
    "Exterior Carpentry",
    "Window Trim",
    "Shutters",
    "Garage Door",
    "Garage Door Trim",
    "Window Screens",
    "Patio Screens",
    "Stucco",
    "Carry Debris",
    "Prime, Prep &Paint Interior",
    "Mask& Prep for paint",
    "Content Manipulation Fee",
    "Trim, Walls",
    "R&R Bath Fixtures",
    "R&R Insulation",
    "R&R Drywall",
    "R&R Suspended Ceiling",
    "R&R Electric",
    "Plumbing",
    "Fence",
    "Misc. Labor",
    "Job Photos"
];

export interface LaborSelection {
    id?: string;
    CartItemId?: string;
    quantity: number;
    unitPrice?: number;
    supplierId?: string;
    supplierName?: string;
}

interface UseLaborMasterDataInitProps {
    measurements: any[];
    attributes?: any[];
    // Setters (owned by the orchestrator hook)
    setLaborSchema: (schema: any[]) => void;
    setCart: (cart: any[]) => void;
    setPriceRanges: (ranges: Record<string, { min: number; max: number }>) => void;
    setIsLoading: (loading: boolean) => void;
    setSelectedValues: (values: Record<string, LaborSelection>) => void;
    initializeDbState: (values: Record<string, LaborSelection>) => void;
}

/**
 * Initialization Hook: Loads labor data from API and hydrates the local UI state.
 */
export default function useLaborMasterDataInit(stateSetters: UseLaborMasterDataInitProps) {
    const { measurements, attributes, setLaborSchema, setCart, setPriceRanges, setIsLoading, setSelectedValues, initializeDbState } = stateSetters;

    const { project } = useProject();
    const { useListByProjectId } = useCart();
    const { useListLaborServices, useListLaborPriceRanges } = useLaborQuery();

    const cartListSvc = useListByProjectId();
    const listLaborServicesSvc = useListLaborServices();
    const listLaborPriceRangesSvc = useListLaborPriceRanges();

    const { evaluateFormula } = useLaborFormulaEvaluator();

    const fetchCartItems = useCallback(async () => {
        if (!project?.id) return [];
        return await cartListSvc.mutateAsync({ ProjectId: project.id });
    }, [project?.id, cartListSvc]);

    const getIdentifiedLaborServices = (allServices: any[]) => {
        const formulaResults = evaluateFormula(measurements, allServices, {}, attributes);
        let identifiedServices = allServices.filter((service: any) => {
            const qty = formulaResults[service.Id];
            return qty !== null && qty !== undefined && qty > 0;
        });

        identifiedServices.sort((a, b) => {
            const indexA = LABOR_ORDER.indexOf(a.Name);
            const indexB = LABOR_ORDER.indexOf(b.Name);
            // If not found in LABOR_ORDER, put it at the end
            if (indexA === -1 && indexB === -1) return a.Name.localeCompare(b.Name);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });

        return {
            identifiedServices,
            formulaResults,
        };
    };

    const parseLaborCartItems = (cartItems: any[]): Record<string, LaborSelection> => {
        const result: Record<string, LaborSelection> = {};
        cartItems
            .filter((item: any) => item.LaborServiceId)
            .forEach((item: any) => {
                result[item.LaborServiceId] = {
                    id: item.Id,
                    CartItemId: item.CartItemId || item.Id,
                    quantity: item.ItemCount || 0,
                    unitPrice: item.UnitPrice || 0,
                    supplierId: item.SupplierId,
                    supplierName: item.SupplierName,
                };
            });
        return result;
    };

    const loadLaborData = useCallback(async () => {
        if (!project?.id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const allServices = listLaborServicesSvc.data ?? [];

            const [cartItems, apiRanges] = await Promise.all([
                fetchCartItems(),
                listLaborPriceRangesSvc.mutateAsync()
            ]);

            const ranges: Record<string, { min: number; max: number }> = {};
            apiRanges?.forEach((item: any) => {
                ranges[item.LaborServiceId] = {
                    min: Number(item.MinPrice),
                    max: Number(item.MaxPrice)
                };
            });
            setPriceRanges(ranges);

            const { identifiedServices, formulaResults } = getIdentifiedLaborServices(allServices);
            setLaborSchema(identifiedServices);

            setCart(cartItems);

            const cartSelections = parseLaborCartItems(cartItems);

            // Seed db memory only with what is actually currently inside the database
            initializeDbState(cartSelections);

            // Merge: cart wins on conflict; formula fills in the rest
            const hydratedSelections: Record<string, LaborSelection> = { ...cartSelections };
            identifiedServices.forEach((service: any) => {
                const calculatedQty = formulaResults[service.Id];
                if (!cartSelections[service.Id] && calculatedQty && calculatedQty > 0) {
                    hydratedSelections[service.Id] = { quantity: calculatedQty, unitPrice: 0 };
                }
            });

            setSelectedValues(hydratedSelections);
        } catch (error) {
            console.error("Error loading labor data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        project?.id,
        measurements,
        listLaborServicesSvc.data,
        fetchCartItems,
        listLaborPriceRangesSvc,
        setLaborSchema,
        setCart,
        setPriceRanges,
        setIsLoading,
        setSelectedValues,
        initializeDbState,
    ]);

    useEffect(() => {
        if (Array.isArray(measurements) && measurements.length > 0 && !listLaborServicesSvc.isLoading) {
            loadLaborData();
        }
    }, [measurements, listLaborServicesSvc.isLoading]);

    return {
        loadLaborData,
        fetchCartItems,
    };
}
